import { useEffect, useRef, useState } from "react";
import type { ReplyDraft, ReplyDraftPut } from "@omnabase/shared";
import { api } from "../../lib/api";

/** A person's reply in progress, kept on the server (the composer-and-fixes round, 2026-11-03; brief rules 1 and 2, the red team's findings 6,
 *  7 and 11). The hook reads the person's kept draft when the ticket opens and writes it through one queue of saves:
 *  - `changed()` after a key: one save ten seconds after the first change (a throttle, not a debounce: typing on does not push it away);
 *  - `saveNow()` on blur, Escape and "Save as draft" (Send's chevron): the composer is read at once, the save waits its turn;
 *  - `settle()` before a send: the queued save is cancelled and the one in flight is awaited, so a late save can never bring a sent text back;
 *  - `discard()`: settles, then deletes; `forget()` after a send (the server deleted the row with the reply).
 *  Nothing is written while the text is blank and nothing was kept; a blank text over a kept one deletes it (the server's rule). Two tabs of the
 *  same person: the last save wins. `stored` is undefined until the first read answers. */
export const AUTOSAVE_MS = 10_000;

export type ReplyDraftHandle = {
  stored: ReplyDraft | null | undefined;
  /** how to read the composer right now (null: no composer) */ reader: (read: () => ReplyDraftPut | null) => void;
  changed: () => void; /** true when a save was queued */ saveNow: () => Promise<boolean>; settle: () => Promise<void>; discard: () => Promise<void>; forget: () => void;
};

export function useReplyDraft(slug: string, number: number, say: (e: unknown) => void): ReplyDraftHandle {
  const url = `/api/v1/w/${slug}/tickets/${number}/reply-draft`;
  const [stored, setStored] = useState<ReplyDraft | null | undefined>(undefined);
  const kept = useRef(false); // a row exists on the server (read, or written in this visit)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flight = useRef<Promise<void>>(Promise.resolve());
  const read = useRef<() => ReplyDraftPut | null>(() => null);
  const cancel = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };

  useEffect(() => {
    let live = true; setStored(undefined); kept.current = false;
    api.get<{ draft: ReplyDraft | null }>(url).then((r) => { if (live) { kept.current = !!r.draft; setStored(r.draft); } }, () => { if (live) setStored(null); });
    return () => { live = false; cancel(); };
  }, [url]);

  const enqueue = async (body: ReplyDraftPut): Promise<boolean> => {
    if (!body.text.trim() && !kept.current) return false; // nothing typed, nothing kept: nothing to write
    kept.current = !!body.text.trim();
    setStored(kept.current ? asKept(body) : null); // at once: a composer opened again before the answer starts from these words
    flight.current = flight.current.then(() => api.put<{ draft: ReplyDraft | null }>(url, body)).then((r) => { kept.current = !!r.draft; setStored(r.draft); }, say);
    await flight.current;
    return true;
  };
  const saveNow = async () => { cancel(); const body = read.current(); return body ? enqueue(body) : false; };
  const settle = async () => { cancel(); await flight.current; };
  return {
    stored,
    reader: (fn) => { read.current = fn; },
    changed: () => { if (!timer.current) timer.current = setTimeout(() => { timer.current = null; void saveNow(); }, AUTOSAVE_MS); },
    saveNow, settle,
    discard: async () => { await settle(); try { await api.del(url); } catch (e) { say(e); } kept.current = false; setStored(null); },
    forget: () => { cancel(); kept.current = false; setStored(null); },
  };
}

/** the kept draft as the server will answer it, for the moment between the save and its answer */
const asKept = (b: ReplyDraftPut): ReplyDraft => ({ html: b.html, text: b.text, to: b.to ?? null, sendAs: b.sendAs ?? null, signature: b.signature ?? true, aiDraftId: b.aiDraftId ?? null, hadFiles: !!b.hadFiles, updatedAt: new Date().toISOString() });

/** the saved reply card's head (the queue-and-chat-column round, 2026-11-03): "Your draft · 2 h ago" for a recent draft (the list's age
 *  words), "Your draft · 3 Oct" for an older one */
export const keptDraftWords = (age: string): string => (/^\d+ (min|h|d)$/.test(age) ? `Your draft · ${age} ago` : `Your draft · ${age}`);
